'use client';

import { Sample } from '@/sharedTypes/Api';

/**
 * Connectors to skip in English names (e.g., "L. van Beethoven" → "Beethoven")
 */
export const CONNECTORS_EN: string[] = ['van', 'de', 'von', 'da', 'di', 'del', 'della'];

/**
 * Connectors to skip in Russian names (e.g., "Л. ван Бетховен" → "Бетховен")
 */
export const CONNECTORS_RU: string[] = ['ван', 'де', 'фон', 'да', 'ди', 'дель', 'делла'];

/**
 * English alphabet (A-Z, 26 letters)
 */
export const ENGLISH_ALPHABET: string[] = [
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M',
  'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'
];

/**
 * Russian alphabet (А-Я, including Ё, 33 letters)
 */
export const RUSSIAN_ALPHABET: string[] = [
  'А', 'Б', 'В', 'Г', 'Д', 'Е', 'Ё', 'Ж', 'З', 'И', 'Й', 'К', 'Л', 'М',
  'Н', 'О', 'П', 'Р', 'С', 'Т', 'У', 'Ф', 'Х', 'Ц', 'Ч', 'Ш', 'Щ', 'Ъ',
  'Ы', 'Ь', 'Э', 'Ю', 'Я'
];

/**
 * Extract the family name from a composer's name
 * @param composerName - Composer name (e.g., "Ludwig van Beethoven", "J.S. Bach")
 * @param lang - Current language ('en' | 'ru')
 * @returns Family name (last name)
 *
 * Examples:
 * EN: "L. van Beethoven" → "Beethoven"
 * EN: "Ludwig van Beethoven" → "Beethoven"
 * EN: "J.S. Bach" → "Bach"
 * RU: "Л. ван Бетховен" → "Бетховен"
 * RU: "Людвиг ван Бетховен" → "Бетховен"
 * RU: "Пётр Ильич Чайковский" → "Чайковский"
 */
export function extractFamilyName(composerName: string, lang: 'en' | 'ru'): string {
  if (!composerName || !composerName.trim()) {
    return '';
  }

  const words = composerName.trim().split(/\s+/);

  if (words.length === 0) {
    return '';
  }

  // For both EN and RU, the family name is typically the last word
  // Work backwards to find the last non-connector word
  for (let i = words.length - 1; i >= 0; i--) {
    const word = words[i];

    // Skip initials with period (e.g., "H.", "A.", "И.", "С.")
    if (/^[A-ZА-ЯЁ]\.$/.test(word)) {
      continue;
    }

    // Skip connectors in English (van, de, von, etc.)
    if (lang === 'en' && CONNECTORS_EN.includes(word.toLowerCase())) {
      continue;
    }

    // Skip connectors in Russian (ван, де, фон, etc.)
    if (lang === 'ru' && CONNECTORS_RU.includes(word.toLowerCase())) {
      continue;
    }

    // This is the family name
    return word;
  }

  // Fallback: return the last word
  return words[words.length - 1];
}

/**
 * Extract the first letter from a composer's family name
 * @param composerName - Composer name (can be full name, formatted name, or just last name)
 * @param lang - Current language ('en' | 'ru')
 * @returns First letter of family name (uppercase)
 *
 * Examples:
 * EN: "Beethoven" → "B"
 * EN: "Ludwig van Beethoven" → "B"
 * EN: "Beethoven, L. van" → "B"
 * RU: "Бах" → "Б"
 * RU: "Пётр Ильич Чайковский" → "Ч"
 * RU: "Бах, И. С." → "Б"
 */
export function extractLetter(composerName: string, lang: 'en' | 'ru'): string {
  if (!composerName || !composerName.trim()) {
    return '';
  }

  // If name is already formatted (contains comma), extract the first word
  if (composerName.includes(',')) {
    const firstWord = composerName.split(',')[0].trim();
    if (firstWord.length > 0) {
      return firstWord[0].toUpperCase();
    }
  }

  // Otherwise, extract family name
  const familyName = extractFamilyName(composerName, lang);

  if (!familyName || familyName.length === 0) {
    return '';
  }

  return familyName[0].toUpperCase();
}

/**
 * Check if a composer name is an arrangement/processing text
 * @param composerName - Composer name to check
 * @returns true if the name indicates an arrangement
 *
 * Examples:
 * EN: "Arranged by N. Rimsky-Korsakov" → true
 * RU: "Обработка Н.Римского-Корсакова" → true
 */
export function isArrangement(composerName: string): boolean {
  if (!composerName || !composerName.trim()) {
    return false;
  }

  const lowerName = composerName.toLowerCase();

  // Check for English arrangement indicators
  if (lowerName.includes('arranged by') ||
      lowerName.includes('arrangement') ||
      lowerName.startsWith('arr.') ||
      lowerName.startsWith('arr ')) {
    return true;
  }

  // Check for Russian arrangement indicators
  if (lowerName.includes('обработка') ||
      lowerName.includes('обраб.') ||
      lowerName.includes('аранжировка')) {
    return true;
  }

  return false;
}

/**
 * Format a composer's name as "LastName [connector], Initials" or just "LastName"
 * @param composerName - Full composer name (e.g., "Johann Sebastian Bach", "Ludwig van Beethoven")
 * @param lang - Current language ('en' | 'ru')
 * @returns Formatted name
 *
 * Examples:
 * EN: "Johann Sebastian Bach" → "Bach, J. S."
 * EN: "Johann Christian Bach" → "Bach, J. C."
 * EN: "Ludwig van Beethoven" → "Beethoven, L. van"
 * EN: "Bach" → "Bach"
 * RU: "Иоганн Себастьян Бах" → "Бах, И. С."
 * RU: "Иоганн Кристиан Бах" → "Бах, И. К."
 * RU: "Людвиг ван Бетховен" → "Бетховен, Л. ван"
 */
export function formatComposerName(composerName: string, lang: 'en' | 'ru'): string {
  if (!composerName || !composerName.trim()) {
    return '';
  }

  const words = composerName.trim().split(/\s+/);

  if (words.length === 0) {
    return '';
  }

  // If only one word, return as is
  if (words.length === 1) {
    return words[0];
  }

  // Find the family name and its position
  const familyName = extractFamilyName(composerName, lang);
  const familyNameIndex = words.findIndex(word => word === familyName);

  if (familyNameIndex === -1) {
    return familyName;
  }

  // Extract initials from words before the family name
  const initials: string[] = [];
  const connectors: string[] = [];

  for (let i = 0; i < familyNameIndex; i++) {
    const word = words[i];

    // Check if word is a connector in English
    if (lang === 'en' && CONNECTORS_EN.includes(word.toLowerCase())) {
      connectors.push(word.toLowerCase());
      continue;
    }

    // Check if word is a connector in Russian
    if (lang === 'ru' && CONNECTORS_RU.includes(word.toLowerCase())) {
      connectors.push(word.toLowerCase());
      continue;
    }

    // Check if word is already an initial (e.g., "J." or "И.")
    if (/^[A-ZА-ЯЁ]\.$/.test(word)) {
      initials.push(word);
    }
    // Extract first letter as initial
    else if (word.length > 0) {
      const firstChar = word[0].toUpperCase();
      initials.push(firstChar + '.');
    }
  }

  // Build formatted name: "LastName, Initials [connector]" or just "LastName"
  if (initials.length > 0 || connectors.length > 0) {
    let formatted = familyName + ', ' + initials.join(' ');
    if (connectors.length > 0) {
      formatted += (initials.length > 0 ? ' ' : '') + connectors.join(' ');
    }
    return formatted;
  }

  return familyName;
}

/**
 * Get unique composers with their sheet counts
 * @param samples - All samples (backend already filters by visibility)
 * @param lang - Current language ('en' | 'ru')
 * @returns Map<formattedComposerName, count>
 *
 * Example:
 * Map {
 *   'Bach, J. S.' => 15,
 *   'Beethoven, L. van' => 3
 * }
 */
export function getComposerCounts(samples: Sample[], lang: 'en' | 'ru'): Map<string, number> {
  const counts = new Map<string, number>();

  // Count all samples passed in (backend already filters by showAll parameter)
  // We trust that the samples array only contains samples that should be visible
  for (const sample of samples) {
    // Skip "Folk Music" / "Народная музыка" category entirely
    const categoryName = lang === 'ru'
      ? sample.category?.ru
      : sample.category?.en;

    if (categoryName) {
      const lowerCategory = categoryName.toLowerCase();
      if (lowerCategory === 'folk music' ||
          lowerCategory === 'народная музыка') {
        continue;
      }
    }

    // Use author field from metadata
    let fullName = lang === 'ru'
      ? sample.metadata?.author?.ru
      : sample.metadata?.author?.en;

    if (!fullName || !fullName.trim()) {
      continue;
    }

    // If author is an arrangement (e.g., "Arranged by N. Rimsky-Korsakov"),
    // use category name instead
    if (isArrangement(fullName)) {
      fullName = categoryName;

      if (!fullName || !fullName.trim()) {
        continue;
      }
    }

    // Format name as "LastName, Initials [connector]"
    const formattedName = formatComposerName(fullName, lang);

    if (!formattedName) {
      continue;
    }

    const currentCount = counts.get(formattedName) || 0;
    counts.set(formattedName, currentCount + 1);
  }

  return counts;
}

/**
 * Get available letters that have at least one composer
 * @param composers - Array of composer names
 * @param lang - Current language
 * @returns Array of letters that should be shown (sorted)
 *
 * Example EN: ['A', 'B', 'C', 'M', 'T'] (only letters with composers)
 * Example RU: ['А', 'Б', 'Г', 'М', 'Т']
 */
export function getAvailableLetters(composers: string[], lang: 'en' | 'ru'): string[] {
  const alphabet = lang === 'ru' ? RUSSIAN_ALPHABET : ENGLISH_ALPHABET;
  const availableSet = new Set<string>();

  for (const composer of composers) {
    const letter = extractLetter(composer, lang);
    if (letter && alphabet.includes(letter)) {
      availableSet.add(letter);
    }
  }

  // Sort the letters according to the alphabet
  const available = Array.from(availableSet);
  available.sort((a, b) => {
    const indexA = alphabet.indexOf(a);
    const indexB = alphabet.indexOf(b);
    return indexA - indexB;
  });

  return available;
}
